import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import posthog from 'posthog-js';
import api from '../services/api';
import { formatDeadlineBadge, dueDateToQuarter } from '../utils/dateHelpers';
import { markQuarterPaid, unmarkQuarterPaid, getQuarterlyPayments } from '../services/quarterlyPayments';

const BLUE       = 'var(--signal)';
const BLUE_DARK  = 'var(--signal-ink)';
const BLUE_LIGHT = 'var(--signal-soft)';
const RED        = 'var(--stop)';
const RED_BG     = 'var(--stop-soft)';
const TEXT1      = 'var(--ink)';
const TEXT2      = 'var(--slate)';
const TEXT3      = 'var(--mute)';

function fmt(n) {
  return '$' + Math.round(n).toLocaleString();
}

function formatDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}


function StickyHeader({ navigate }) {
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 100,
      height: 56, backgroundColor: 'var(--card)',
      borderBottom: '0.5px solid rgba(0,0,0,0.12)',
      display: 'flex', alignItems: 'center',
      padding: '0 20px', gap: 10,
    }}>
      <span
        onClick={() => navigate('/dashboard')}
        style={{ fontSize: 18, color: TEXT2, cursor: 'pointer', lineHeight: 1, paddingRight: 4, userSelect: 'none' }}
      >
        ←
      </span>
      <span style={{ fontFamily: 'var(--font-serif)', fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.3px' }}>
        <span style={{ color: TEXT1 }}>Tru</span>
        <span style={{ color: BLUE }}>Flow</span>
      </span>
    </div>
  );
}

const CHECKLIST_ITEMS = [
  'Social Security Number or ITIN',
  'Date of birth',
  'Bank routing and account number',
  'Prior year tax return (recommended)',
];

const STEPS = [
  'Tap "Make a Payment" on the IRS Direct Pay page.',
  'Select Estimated Tax → 1040-ES → current tax year.',
  'Enter your SSN, date of birth, and name exactly as on your tax return.',
  'Verify your identity using your prior year AGI — Line 11 of Form 1040.',
  null, // injected with amount
  'Enter your bank routing number and account number.',
  null, // injected with due date
  'Review all fields carefully before submitting.',
  'Screenshot your confirmation number — the IRS does not send a receipt by email.',
];

export default function IRSPayment() {
  const navigate = useNavigate();
  const [amount, setAmount]         = useState(null);
  const [dueDate, setDueDate]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [quarterInfo, setQuarterInfo] = useState(null);
  const [isPaid, setIsPaid]         = useState(false);
  const [markLoading, setMarkLoading] = useState(false);
  const [markError, setMarkError]   = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const res = await api.get('/tax/calculate');
        if (cancelled) return;
        const reserveAmount = res.data?.recommended_reserve ?? null;
        const due = res.data?.next_due_date ?? res.data?.due_date ?? null;
        setAmount(reserveAmount);
        setDueDate(due);

        const qi = dueDateToQuarter(due);
        setQuarterInfo(qi);

        if (qi) {
          try {
            const payments = await getQuarterlyPayments(qi.tax_year);
            if (!cancelled) {
              const alreadyPaid = payments.some(
                (p) => p.tax_year === qi.tax_year && p.quarter === qi.quarter
              );
              if (alreadyPaid) setIsPaid(true);
            }
          } catch {}
        }
      } catch {}
      if (!cancelled) setLoading(false);
    }

    load();
    return () => { cancelled = true; };
  }, []);

  async function handleMarkPaid() {
    setMarkLoading(true);
    setMarkError(null);
    try {
      await markQuarterPaid(quarterInfo.tax_year, quarterInfo.quarter);
      setIsPaid(true);
      posthog.capture('quarter_marked_paid', { tax_year: quarterInfo.tax_year, quarter: quarterInfo.quarter });
    } catch {
      setMarkError("Couldn't mark as paid. Please try again.");
    }
    setMarkLoading(false);
  }

  async function handleUndo() {
    setMarkLoading(true);
    setMarkError(null);
    try {
      await unmarkQuarterPaid(quarterInfo.tax_year, quarterInfo.quarter);
      setIsPaid(false);
      posthog.capture('quarter_marked_unpaid', { tax_year: quarterInfo.tax_year, quarter: quarterInfo.quarter });
    } catch {
      setMarkError("Couldn't undo. Please try again.");
    }
    setMarkLoading(false);
  }

  const formattedDate = formatDate(dueDate);
  const deadlineBadge = formatDeadlineBadge(dueDate);
  const badgeAlert = deadlineBadge?.urgency === 'critical' || deadlineBadge?.urgency === 'overdue';

  function stepText(i) {
    if (i === 4) {
      const amtStr = amount != null ? `TruFlow's estimate: ${fmt(amount)}.` : "TruFlow's estimate is shown above.";
      return `Enter your payment amount. ${amtStr}`;
    }
    if (i === 6) {
      const dateStr = formattedDate ?? 'your next quarterly deadline';
      return `Select your payment date. Deadline: ${dateStr}.`;
    }
    return STEPS[i];
  }

  if (loading) {
    return (
      <div style={{ backgroundColor: 'var(--paper)', minHeight: '100vh' }}>
        <StickyHeader navigate={navigate} />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid var(--signal-soft)', borderTopColor: BLUE, animation: 'spin 0.7s linear infinite' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: 'var(--paper)', minHeight: '100vh' }}>
      <StickyHeader navigate={navigate} />

      <div style={{ maxWidth: 480, margin: '0 auto', padding: '20px 16px 40px' }}>
        <h1 style={{ margin: '0 0 6px', fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 700, color: TEXT1 }}>
          Before you pay
        </h1>
        <p style={{ margin: '0 0 20px', fontSize: 13, color: TEXT2, lineHeight: 1.5 }}>
          Here's what you'll need before opening IRS Direct Pay.
        </p>

        {/* Amount card */}
        <div style={{
          backgroundColor: BLUE_LIGHT, borderRadius: 10,
          padding: 14, marginBottom: dueDate ? 10 : 16,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: BLUE_DARK }}>
            Your estimated payment
          </span>
          {amount != null ? (
            <span style={{ fontFamily: 'var(--font-serif)', fontSize: 28, fontWeight: 700, color: BLUE }}>
              {fmt(amount)}
            </span>
          ) : (
            <span style={{ fontSize: 13, color: TEXT2 }}>
              Complete tax setup first
            </span>
          )}
        </div>

        {/* Due date badge */}
        {deadlineBadge && (
          <div style={{ marginBottom: 16 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center',
              padding: '4px 12px', borderRadius: 20, fontSize: 12,
              fontWeight: 500,
              ...(badgeAlert
                ? { backgroundColor: RED_BG, border: '0.5px solid var(--stop-border)', color: RED }
                : { backgroundColor: BLUE_LIGHT, border: '0.5px solid rgba(24,95,165,0.2)', color: BLUE_DARK }
              ),
            }}>
              {deadlineBadge.label}
            </span>
          </div>
        )}

        {/* Gather these first */}
        <div style={{
          backgroundColor: 'var(--card)',
          boxShadow: 'var(--sh-card)',
          borderRadius: 'var(--r-card)', padding: 16, marginBottom: 14,
        }}>
          <p style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 600, color: TEXT1 }}>
            Gather these first
          </p>
          {CHECKLIST_ITEMS.map((item, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 0',
              borderBottom: i < CHECKLIST_ITEMS.length - 1 ? '0.5px solid rgba(0,0,0,0.07)' : 'none',
            }}>
              <div style={{
                width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                border: '1.5px solid rgba(0,0,0,0.18)',
                backgroundColor: 'var(--card)',
              }} />
              <span style={{ fontSize: 13, color: TEXT2, lineHeight: 1.4 }}>
                {item}
              </span>
            </div>
          ))}
        </div>

        {/* Step by step */}
        <div style={{
          backgroundColor: 'var(--card)',
          boxShadow: 'var(--sh-card)',
          borderRadius: 'var(--r-card)', padding: 16, marginBottom: 16,
        }}>
          <p style={{ margin: '0 0 14px', fontSize: 13, fontWeight: 600, color: TEXT1 }}>
            Follow these steps on the IRS site
          </p>
          {STEPS.map((_, i) => (
            <div key={i} style={{
              display: 'flex', gap: 12, alignItems: 'flex-start',
              padding: '8px 0',
              borderBottom: i < STEPS.length - 1 ? '0.5px solid rgba(0,0,0,0.07)' : 'none',
            }}>
              <div style={{
                width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                backgroundColor: BLUE_LIGHT, color: BLUE_DARK,
                fontSize: 11, fontWeight: 600,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginTop: 1,
              }}>
                {i + 1}
              </div>
              <span style={{ fontSize: 13, color: TEXT2, lineHeight: 1.5 }}>
                {stepText(i)}
              </span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <button
          onClick={() => window.open('https://directpay.irs.gov', '_blank', 'noopener,noreferrer')}
          style={{
            width: '100%', padding: 13,
            backgroundColor: BLUE, color: 'var(--on-signal)',
            border: 'none', borderRadius: 10,
            fontSize: 14, fontWeight: 500,
            cursor: 'pointer',
            marginTop: 16,
          }}
        >
          Open IRS Direct Pay →
        </button>

        {quarterInfo && (
          <div style={{
            backgroundColor: 'var(--card)',
            boxShadow: 'var(--sh-card)',
            borderRadius: 'var(--r-card)', padding: 16, marginTop: 12,
          }}>
            <p style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 600, color: TEXT1 }}>
              After you've paid the IRS
            </p>
            <p style={{ margin: '0 0 14px', fontSize: 12, color: TEXT2 }}>
              Confirm your payment to update your dashboard.
            </p>

            {isPaid ? (
              <div>
                <div style={{ backgroundColor: 'var(--go-soft)', borderRadius: 10, padding: 12 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--go)' }}>
                    ✓ Q{quarterInfo.quarter} {quarterInfo.tax_year} marked as paid
                  </span>
                </div>
                {markError && (
                  <p style={{ fontSize: 12, color: RED, marginTop: 8, marginBottom: 0 }}>
                    {markError}
                  </p>
                )}
                <button
                  onClick={handleUndo}
                  disabled={markLoading}
                  style={{
                    background: 'transparent', border: 'none',
                    color: BLUE, fontSize: 13, cursor: markLoading ? 'default' : 'pointer',
                    padding: 0, marginTop: 8,
                    opacity: markLoading ? 0.6 : 1,
                  }}
                >
                  {markLoading ? 'Undoing...' : 'Undo'}
                </button>
              </div>
            ) : (
              <div>
                <button
                  onClick={handleMarkPaid}
                  disabled={markLoading}
                  style={{
                    width: '100%', padding: 13,
                    backgroundColor: 'var(--go)', color: 'var(--on-signal)',
                    border: 'none', borderRadius: 10,
                    fontSize: 14, fontWeight: 500,
                    cursor: markLoading ? 'default' : 'pointer',
                    opacity: markLoading ? 0.6 : 1,
                  }}
                >
                  {markLoading
                    ? 'Marking as paid...'
                    : `I've paid — mark Q${quarterInfo.quarter} ${quarterInfo.tax_year} as paid`}
                </button>
                {markError && (
                  <p style={{ fontSize: 12, color: RED, marginTop: 8, marginBottom: 0 }}>
                    {markError}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <p style={{
          textAlign: 'center', fontSize: 10, color: TEXT3,
          margin: '14px 0 0', lineHeight: 1.6,
        }}>
          TruFlow is not affiliated with the IRS. Your payment goes directly to the IRS — TruFlow never handles your money.
        </p>
      </div>
    </div>
  );
}
